/**
 * TEMP-H-010: Export Event Card Component
 * 
 * Specialized renderer for export_preview stage events with preview artifacts,
 * format information, and export metadata display.
 */

import React, { useState, memo } from 'react';
import { 
  FileVideo, 
  Play, 
  Pause, 
  Download,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Clock,
  Hash,
  Monitor,
  Settings
} from 'lucide-react';
import type { TimelineEvent, ArtifactRef } from '@/types/execution-events';
import { STAGE_DISPLAY_CONFIG } from '@/types/execution-events';

interface ExportEventCardProps {
  event: TimelineEvent;
  compact?: boolean;
  showArtifacts?: boolean;
}

interface ExportArtifactProps {
  artifact: ArtifactRef;
  isPlaying?: boolean;
  onPlayToggle?: () => void;
}

const ExportArtifact: React.FC<ExportArtifactProps> = ({ artifact, isPlaying, onPlayToggle }) => {
  const getFormatColor = (format?: string) => {
    switch (format?.toLowerCase()) {
      case 'mp4': return 'bg-green-500/20 text-green-300';
      case 'mov': return 'bg-blue-500/20 text-blue-300';
      case 'webm': return 'bg-purple-500/20 text-purple-300';
      case 'avi': return 'bg-orange-500/20 text-orange-300';
      default: return 'bg-gray-500/20 text-gray-300';
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatSize = (bytes?: number) => {
    if (!bytes) return '--';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1000) {
      return `${(mb / 1024).toFixed(1)}GB`;
    }
    return `${mb.toFixed(1)}MB`;
  };

  const formatResolution = (dimensions?: { width: number; height: number }) => {
    if (!dimensions) return '--';
    return `${dimensions.width}x${dimensions.height}`;
  };

  // Extract format from name or type
  const format = artifact.name.split('.').pop()?.toUpperCase() || 'VIDEO';

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-800/30 rounded-lg border border-gray-700">
      {/* Thumbnail Preview (if available) */}
      {artifact.thumbnail && (
        <div className="w-16 h-12 flex-shrink-0 bg-gray-700 rounded overflow-hidden">
          <img
            src={artifact.thumbnail}
            alt={`Preview of ${artifact.name}`}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Export Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h5 className="text-sm font-medium text-white truncate">{artifact.name}</h5>
          <span className={`px-2 py-0.5 text-xs rounded-full ${getFormatColor(format)}`}>
            {format}
          </span>
        </div>
        
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatDuration(artifact.duration)}
          </span>
          <span>{formatSize(artifact.size)}</span>
          {artifact.dimensions && (
            <span className="flex items-center gap-1">
              <Monitor className="w-3 h-3" />
              {formatResolution(artifact.dimensions)}
            </span>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {artifact.publicUrl && artifact.type === 'video' && (
          <button
            onClick={onPlayToggle}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4 text-amber-400" />
            ) : (
              <Play className="w-4 h-4 text-amber-400" />
            )}
          </button>
        )}
        
        {artifact.publicUrl && (
          <a
            href={artifact.publicUrl}
            download={artifact.name}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Download"
          >
            <Download className="w-4 h-4 text-gray-400" />
          </a>
        )}
        
        {artifact.publicUrl && (
          <a
            href={artifact.publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Open in new tab"
          >
            <ExternalLink className="w-4 h-4 text-gray-400" />
          </a>
        )}
      </div>
    </div>
  );
};

export const ExportEventCard = memo(function ExportEventCard({
  event,
  compact = false,
  showArtifacts = true
}: ExportEventCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [playingArtifact, setPlayingArtifact] = useState<string | null>(null);

  // Get stage configuration
  const stageConfig = event.stage ? STAGE_DISPLAY_CONFIG[event.stage] : null;
  const stageColor = stageConfig?.color || 'amber';

  // Get stage-specific color classes
  const getColorClasses = (color: string) => {
    const colorMap: Record<string, { border: string; bg: string; text: string; icon: string }> = {
      amber: { border: 'border-l-amber-500', bg: 'bg-amber-500/20', text: 'text-amber-400', icon: 'text-amber-400' },
      cyan: { border: 'border-l-cyan-500', bg: 'bg-cyan-500/20', text: 'text-cyan-400', icon: 'text-cyan-400' },
      green: { border: 'border-l-green-500', bg: 'bg-green-500/20', text: 'text-green-400', icon: 'text-green-400' },
      blue: { border: 'border-l-blue-500', bg: 'bg-blue-500/20', text: 'text-blue-400', icon: 'text-blue-400' },
    };
    return colorMap[color] || colorMap.amber;
  };

  const colorClasses = getColorClasses(stageColor);

  // Filter export artifacts
  const exportArtifacts = event.artifactRefs.filter(artifact => 
    artifact.type === 'video' || artifact.type === 'image'
  );

  // Get export metadata from event payload
  const exportMetadata = event.payload?.exportMetadata || {};
  const format = exportMetadata.format || event.payload?.format;
  const resolution = exportMetadata.resolution || event.payload?.resolution;
  const preset = exportMetadata.preset || event.payload?.preset;

  const handlePlayToggle = (artifactId: string) => {
    setPlayingArtifact(current => current === artifactId ? null : artifactId);
  };

  return (
    <div className={`border-l-2 ${colorClasses.border} bg-gray-900/50`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className={`mt-1 p-2 ${colorClasses.bg} rounded-lg`}>
            <FileVideo className={`w-4 h-4 ${colorClasses.icon}`} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-white flex items-center gap-2">
                  {event.displayTitle}
                  {exportArtifacts.length > 0 && !compact && (
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="opacity-70 hover:opacity-100 transition-opacity"
                      aria-expanded={isExpanded}
                      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} export artifacts`}
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronRight className="w-3 h-3" />
                      )}
                    </button>
                  )}
                </h4>
                {event.displayDescription && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {event.displayDescription}
                  </p>
                )}
              </div>
              
              {/* Export Metadata */}
              <div className="flex items-center gap-2 text-xs text-gray-400">
                {format && (
                  <span className={`px-2 py-1 ${colorClasses.bg} ${colorClasses.text} rounded`}>
                    {format}
                  </span>
                )}
                {resolution && (
                  <span className="flex items-center gap-1">
                    <Monitor className="w-3 h-3" />
                    {resolution}
                  </span>
                )}
                {preset && (
                  <span className="flex items-center gap-1">
                    <Settings className="w-3 h-3" />
                    {preset}
                  </span>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            {!compact && showArtifacts && exportArtifacts.length > 0 && (
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-2">
                <span className="flex items-center gap-1">
                  <FileVideo className="w-3 h-3" />
                  {exportArtifacts.length} export{exportArtifacts.length !== 1 ? 's' : ''}
                </span>
                {event.correlation_id && (
                  <span className="flex items-center gap-1">
                    <Hash className="w-3 h-3" />
                    {event.correlation_id.slice(0, 8)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Export Artifacts */}
        {showArtifacts && exportArtifacts.length > 0 && (isExpanded || compact) && (
          <div className="space-y-2">
            <h5 className="text-xs font-medium text-gray-400">
              Export Artifacts ({exportArtifacts.length})
            </h5>
            {exportArtifacts.map((artifact) => (
              <ExportArtifact
                key={artifact.id}
                artifact={artifact}
                isPlaying={playingArtifact === artifact.id}
                onPlayToggle={() => handlePlayToggle(artifact.id)}
              />
            ))}
          </div>
        )}

        {/* Compact Artifacts Preview */}
        {showArtifacts && exportArtifacts.length > 0 && !isExpanded && !compact && (
          <div className="mt-2">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>Click to view {exportArtifacts.length} export artifacts</span>
              {exportArtifacts.slice(0, 3).map(artifact => {
                const format = artifact.name.split('.').pop()?.toUpperCase();
                return format && (
                  <span key={artifact.id} className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-400">
                    {format}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default ExportEventCard;